/**
 * 生成設定的初始值（純函式）。
 *
 * 預設值由**當前工具包的題目**決定，不由前端硬編。
 * 工具包的題目可以完全自訂——那六個內建題目只是可選的預設題目，不是必備，
 * 一個工具包可以一個都不用。硬編初始值會讓沒被宣告的題目留下答案，
 * 那些答案不會進入 prompt（後端只走 model.questions），卻會被存進生成紀錄，
 * 還原時被誤報為「找不到對應題目」。
 *
 * 這裡不對任何特定 question id 做特判。
 */

export interface DefaultValueQuestion {
  id: string;
  defaultValue?: string | boolean;
}

/**
 * 取出工具包題目宣告的預設值。
 * 沒有宣告 defaultValue 的題目就沒有初始答案（不會補空字串之類的佔位值）。
 */
export function buildDefaultAnswers(
  questions: DefaultValueQuestion[] | undefined
): Record<string, string | boolean> {
  const defaults: Record<string, string | boolean> = {};
  for (const q of questions ?? []) {
    if (q.defaultValue !== undefined) defaults[q.id] = q.defaultValue;
  }
  return defaults;
}

/** 尚未作答：未定義、空字串或 null 都算 */
function isUnanswered(value: unknown): boolean {
  return value === undefined || value === '' || value === null;
}

/**
 * 把工具包的預設值補進尚未作答的題目。
 * 使用者已經填過的內容不會被覆蓋。
 */
export function withDefaultAnswers<T extends Record<string, unknown>>(
  current: T,
  questions: DefaultValueQuestion[] | undefined
): T {
  const defaults = buildDefaultAnswers(questions);
  if (Object.keys(defaults).length === 0) return current;

  const next = { ...current } as Record<string, unknown>;
  for (const [id, value] of Object.entries(defaults)) {
    if (isUnanswered(next[id])) next[id] = value;
  }
  return next as T;
}
